import { QueryClient, useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { useMemo } from 'react';
import { create } from 'zustand';

import type { MealResponse } from '@/api/generated/MNT';
import type { MealItem } from '@/apiClients/mpq';
import { type RegionalApis } from '@/apiClients/regionalApis';
import type { FoodSearchItem } from '@/client-search/food-search-index';
import { ApiRegion } from '@/config';
import { useFeatures } from '@/context/FeatureContext';
import { useRegionalApis } from '@/context/RegionScope';
import { registerSessionReset } from '@/context/sessionClear';
import type { ExternalFoodSearchResult } from '@/services/foodSearch/useFoodSearch';

// Hover state and trigger hook for the food details popup. Kept apart from the popup UI so the many
// places that only attach hover props (and the app shell) do not pull the whole popup into their chunk.

export type FoodDetailsPopupProps = {
  anchorTo?: 'mouse' | 'element';
  internalFoodName?: string;
  mealItem?: MealItem;
  foodSearchRes?: FoodSearchItem;
  externalFoodRes?: ExternalFoodSearchResult;
  showRecentMealsWithQueueId?: number;
  handleMealClick?: (mealResponse: MealResponse, index: number, source?: string) => void;
  regionScope?: { apis: RegionalApis; queryClient: QueryClient };
  rxfood_id?: string | null;
};

export const useFoodDetailsPopupStore = create<{
  isMouseOverPopup: boolean;
  isMouseOverTarget: HTMLElement | null;
  componentPosition: DOMRect | null;
  mousePosition: { x: number; y: number } | null;
  isPopupShown: boolean;
  props: FoodDetailsPopupProps | null;
}>(() => ({
  isMouseOverPopup: false,
  isMouseOverTarget: null,
  componentPosition: null,
  mousePosition: null,
  isPopupShown: false,
  props: null,
}));

// Grace period (ms) before the popup closes after the pointer leaves the target
// or the popup itself. This gives the pointer time to travel across the gap
// between the ⓘ trigger and the popup without it disappearing mid-move.
const POPUP_CLOSE_DELAY_MS = 300;

let popupCloseTimer: ReturnType<typeof setTimeout> | null = null;

export const cancelScheduledPopupClose = () => {
  if (popupCloseTimer) {
    clearTimeout(popupCloseTimer);
    popupCloseTimer = null;
  }
};

export const scheduledPopupClose = () => {
  cancelScheduledPopupClose();
  popupCloseTimer = setTimeout(() => {
    popupCloseTimer = null;
    useFoodDetailsPopupStore.setState({
      isMouseOverPopup: false,
      isMouseOverTarget: null,
      isPopupShown: false,
    });
  }, POPUP_CLOSE_DELAY_MS);
};

/** Drop patient metadata and captured regional clients when a session ends. */
export const clearFoodDetailsPopup = () => {
  cancelScheduledPopupClose();
  useFoodDetailsPopupStore.setState(useFoodDetailsPopupStore.getInitialState());
};
registerSessionReset(clearFoodDetailsPopup);

/*
  Note: the popup will be stuck if the element hovered over is unmounted
  this is normally caused by keyboard events
  e.g. a drawer closed by pressing esc key or search results changed while user is typing

  Easy fix is to use forceClear() along with these events
  For examples, search for forceClear in FoodDrawer.tsx
*/
export const useFoodDetailsPopup = () => {
  const flags = useFeatures();
  const regionalApis = useRegionalApis();
  const regionQueryClient = useQueryClient();
  const forceClear = clearFoodDetailsPopup;

  return useMemo(() => {
    const regionScope =
      regionalApis.region === ApiRegion.CA ? undefined : { apis: regionalApis, queryClient: regionQueryClient };
    return {
      forceClear: forceClear,
      getShowOnHoverProps: (props: FoodDetailsPopupProps) => {
        if (!flags.food_details_popup) {
          return {
            onMouseOver: () => {},
            onMouseLeave: () => {},
            onWheel: () => {},
          };
        }

        return {
          onMouseOver: event => {
            cancelScheduledPopupClose();
            // Only skip when the popup is already showing THIS target. Skipping on
            // any shown popup would leave it stuck on the previous item when the
            // pointer moves between targets within the close grace period (the
            // pending close that used to reset isPopupShown was just cancelled).
            const state = useFoodDetailsPopupStore.getState();
            if (state.isPopupShown && state.isMouseOverTarget === event.currentTarget) {
              return;
            }
            const currPosition = event.currentTarget.getBoundingClientRect();
            useFoodDetailsPopupStore.setState({
              props: { ...props, regionScope },
              isMouseOverTarget: event.currentTarget,
              componentPosition: currPosition,
              mousePosition: {
                x: event.clientX,
                y: event.clientY,
              },
            });
          },
          onMouseLeave: event => {
            // Defer the close so the pointer can travel to the popup (whose
            // onMouseEnter cancels this) without it disappearing mid-move.
            if (event.currentTarget == useFoodDetailsPopupStore.getState().isMouseOverTarget) {
              scheduledPopupClose();
            }
          },
          onWheel: () => {
            forceClear();
          },
        } satisfies Pick<React.HTMLAttributes<HTMLElement>, 'onMouseOver' | 'onMouseLeave' | 'onWheel'>;
      },
    };
  }, [flags.food_details_popup, regionalApis, regionQueryClient]);
};
